import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { HttpStatusCode } from 'axios';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { AgentOnboardingService } from '../agent-onboarding-service/agent-onboarding.service';
import { normalizeAuditLogEntry, OnboardingAuditLog, OnboardingAuditLogDialogData } from '../agent-onboarding-interfaces/onboarding-audit-log.interface';

@Component({
  selector: 'app-onboarding-audit-log-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatDividerModule,
    MatTooltipModule
  ],
  // Without `displayDefaultIndicatorType: false` the CDK stepper ignores a step's
  // custom `state` and always renders number/edit/done — so the per-status icons
  // below would never appear. Provided on the component so the steppers on the
  // register and forgot-password pages keep Material's default behaviour.
  providers: [
    { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } }
  ],
  templateUrl: './onboarding-audit-log.dialog.html',
  styleUrl: './onboarding-audit-log.dialog.scss'
})
export class OnboardingAuditLogDialog implements OnInit {
  private router = inject(Router);
  private snackBarService = inject(SnackbarService);
  private agentOnboardingService = inject(AgentOnboardingService);

  protected setSprinner = signal(false);
  protected auditLog = signal<OnboardingAuditLog[]>([]);
  /** Set when the timeline could not be loaded, shown in place of the stepper. */
  protected loadError = signal<string>('');
  
  protected applicantName: string = "";
  protected designation: string = "";
  protected currentStatus: string = "";

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public matData: OnboardingAuditLogDialogData
  ) { }

  async ngOnInit(): Promise<void> {
    this.applicantName = this.matData.applicantName;
    this.designation = this.matData.designation;
    this.currentStatus = this.matData.currentStatus;

    await this.getOnboardingAuditLog(this.matData.id);
  }

  private async getOnboardingAuditLog(id: number): Promise<void> {
    this.setSprinner.set(true);

    const responseData = await this.agentOnboardingService
      .getOnboardingAuditLog(id)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        return error;
      });

    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {
      const rows: Record<string, unknown>[] = responseData.data;
      this.auditLog.set(
        rows
          .map((row) => normalizeAuditLogEntry(row))
          .filter((entry): entry is OnboardingAuditLog => entry !== null)
          .sort((a, b) => this.toTime(a.date) - this.toTime(b.date))
      );
      return;
    }

    if (responseData.code === HttpStatusCode.Unauthorized) {
      this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
      this.router.navigate(['/login']);
      return;
    }

    // The failure is reported inside the dialog rather than through a snackbar —
    // the dialog is already on screen and a toast behind it reads as noise.
    this.loadError.set("We couldn’t load the onboarding history. Please try again.");
  }

  /** Undated entries sort last rather than jumping to 1970. */
  private toTime(actionDate: string): number {
    const parsed = Date.parse(actionDate);
    return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
  }

  /**
   * `DD/MM/YYYY HH:mm`, or '' when the timestamp is missing or unparseable.
   * Formatted here rather than with the `date` pipe because the pipe throws on a
   * string it cannot parse, which would blank the whole dialog — and the shape
   * of `actionDate` is not yet confirmed against the backend.
   */
  protected formatTimestamp(actionDate: string): string {
    const time = Date.parse(actionDate);
    if (!actionDate || Number.isNaN(time)) {
      return '';
    }
    const parsed = new Date(time);
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()}`
      + ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  }

  /** Reuses the status palette from the onboarding table so the two screens read alike. */
  protected getStatusClass(status: string): string {
    switch (status) {
      case 'CREATE_ONBOARDING_DRAFT': return 'status-draft';
      case 'Submit of Application': return 'status-submission';
      case 'Onboarding Processing': return 'status-processing';
      case 'DSU Final Assessment': return 'status-final';
      case 'DSU Initial Assessment': return 'status-initial';
      case 'Return to candidate': return 'status-submission';
      case 'Candidate submitted application for review': return 'status-review';
      case 'Introducer Indiviual  submitted application for review': return 'status-review';
      case 'Introducer Corporate  submitted application for review': return 'status-review';
      case 'mgr submit decalaration': return 'status-processing';
      case 'Rectification': return 'status-rect';
      case 'Candidate Update (Due Diligence)': return 'status-rect';
      case 'Candidate Update (Assessment)': return 'status-rect';
      case 'Onboarding team submitting declaration': return 'status-initial';
      case 'mgr submit from rectification decalaration': return 'status-initial';     
      case 'Onboarding Introducer team submitting declaration': return 'status-final';
      case 'dsu submitting decalaration': return 'status-interview';
      case 'BDM submitting decalaration': return 'status-final';
      case 'dsu submit decalaration': return 'status-approval';
      case 'dsu mgr submit decalaration': return 'status-completed';
      case 'Return to Manager': return 'status-review';
      default: return '';
    }
  }

  protected getStatusIcon(status: string): string {
    switch (status) {
      case 'CREATE_ONBOARDING_DRAFT': return 'edit_document';
      case 'Submit of Application': return 'schedule';
      case 'Onboarding Processing': return 'autorenew';
      case 'DSU Final Assessment': return 'assignment_turned_in';
      case 'DSU Initial Assessment': return 'assignment';
      case 'Return to candidate': return 'schedule';
      case 'Candidate submitted application for review': return 'rate_review';
      case 'Introducer Indiviual  submitted application for review': return 'rate_review';
      case 'Introducer Corporate  submitted application for review': return 'rate_review';
      case 'mgr submit decalaration': return 'autorenew';
      case 'Rectification': return 'edit';
      case 'Candidate Update (Due Diligence)': return 'edit';
      case 'Candidate Update (Assessment)': return 'edit';
      case 'Onboarding team submitting declaration': return 'assignment';
      case 'mgr submit from rectification decalaration': return 'assignment';
      case 'Onboarding Introducer team submitting declaration': return 'assignment_turned_in';
      case 'dsu submitting decalaration': return 'forum';
      case 'BDM submitting decalaration': return 'assignment_turned_in';
      case 'dsu submit decalaration': return 'pending_actions';
      case 'dsu mgr submit decalaration': return 'task_alt';
      case 'Return to Manager': return 'rate_review';
      default: return 'help';
    }
  }

  protected getStatusLabel(status: string): string {
    switch (status) {
      case 'CREATE_ONBOARDING_DRAFT': return 'Draft Created';
      case 'Submit of Application': return 'Candidate Submission';
      case 'Onboarding Processing': return 'Onboarding Processing';
      case 'DSU Final Assessment': return 'DSU Final Assessment';
      case 'DSU Initial Assessment': return 'DSU Initial Assessment';
      case 'Return to candidate': return 'Candidate Submission';
      case 'Candidate submitted application for review': return 'Manager Review';
      case 'Introducer Indiviual  submitted application for review': return 'Manager Review';
      case 'Introducer Corporate  submitted application for review': return 'Manager Review';
      case 'mgr submit decalaration': return 'Onboarding Processing';
      case 'Rectification': return 'Rectification';
      case 'Candidate Update (Due Diligence)': return 'Candidate Update (Due Diligence)';
      case 'Candidate Update (Assessment)': return 'Candidate Update (Assessment)';
      case 'Onboarding team submitting declaration': return 'DSU Initial Assessment';
      case 'mgr submit from rectification decalaration': return 'DSU Initial Assessment';
      case 'Onboarding Introducer team submitting declaration': return 'DSU Final Assessment';
      case 'dsu submitting decalaration': return 'DM Interview';
      case 'BDM submitting decalaration': return 'DSU Final Assessment';
      case 'dsu submit decalaration': return 'DSU Manager Approval';
      case 'dsu mgr submit decalaration': return 'Onboarding Completed';
      case 'Return to Manager': return 'Manager Review';
      default: return 'help';
    }
  }

  protected getRoleLabel(userRole: string) {
    return userRole === 'BDM' ? 'DM' : userRole === 'newrep' ? 'Candidate' : userRole;
  }
}
